import { ChevronBottomOutlineIcon, ChevronTopOutlineIcon } from '@ikyucom/design-system-icons-react'
import { NoImage, SelectBox } from 'macaron-frontend-ui'
import React, { useCallback } from 'react'
import Image from '~/components/image/Image'
import { StackableFusuma } from '~/components/modal/StackableFusuma'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import type { ReserveOptionMenuInput } from '~/lib/graphql/graphql'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { useBack, useReserveOverlay } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { hstack } from '~/styled-system/patterns'
import { Button } from '~/ui'
import { useSetOptionMenu } from '../store/optionMenu'
import type { ReserveInputOptionMenu } from '../store/stateMachine/state'
import { useMenuCard } from './useMenuCard'
import { scope, useSelectOptionMenus } from './useSelectOptionMenus'

export function OptionMenusFusuma() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_OPTION')
  if (context === undefined) {
    return null
  }
  return (
    <scope.Provider>
      <StackableFusuma title="オプション追加" willBeUnmounted={willBeUnmounted}>
        <SelectOptionMenus />
      </StackableFusuma>
    </scope.Provider>
  )
}

export function OptionMenusModal() {
  const [context, willBeUnmounted] = useReserveOverlay('NEW_RESERVE_INPUT_OPTION')
  if (context === undefined) {
    return null
  }
  return (
    <scope.Provider>
      <StackableSlideUpModal willBeUnmounted={willBeUnmounted}>
        <SelectOptionMenus />
      </StackableSlideUpModal>
    </scope.Provider>
  )
}

const selectOptionMenusStyles = sva({
  slots: ['container', 'heading', 'title', 'content', 'note'],
  base: {
    container: {
      textAlign: 'left',
      pb: '12',
      _pc: {
        spaceY: '5',
        pb: '0',
      },
    },
    heading: {
      px: '3',
      pt: '3',
      _pc: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '6',
        pt: '6',
      },
    },
    title: {
      _pc: {
        textAlign: 'left',
        fontSize: '2xl',
        fontWeight: 'bold',
      },
    },
    content: {
      m: '3',
      pb: '16',
      spaceY: '3',
      _pc: {
        mx: '0',
        px: '6',
        pb: '12',
      },
    },
    note: {
      fontSize: 'xs',
      color: 'gray.600',
      textAlign: 'right',
      _pc: {
        fontSize: 'sm',
      },
    },
  },
})

function SelectOptionMenus() {
  const { options, selectedOptions, onSelect } = useSelectOptionMenus()
  const isPc = useIsPC()
  const Submit = isPc ? PcSubmit : SdSubmit
  const MenuCard = isPc ? PcMenuCard : SdMenuCard
  const classes = selectOptionMenusStyles()
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        {isPc && <h2 className={classes.title}>オプション追加</h2>}
        <div className={classes.note}>※写真はイメージです</div>
      </div>
      <div className={classes.content}>
        {options.map((option) => (
          <MenuCard
            key={option.optionMenu.optionMenuId}
            option={option}
            selectedOptions={selectedOptions}
            onSelect={onSelect}
          />
        ))}
      </div>
      <Submit selectedOptions={selectedOptions} />
    </div>
  )
}

const sdMenuCardStyles = sva({
  slots: [
    'container',
    'image',
    'content',
    'name',
    'detail',
    'button',
    'select',
    'price',
    'selectbox',
    'description',
  ],
  base: {
    container: {
      borderRadius: 'md',
      border: 'medium',
      borderColor: 'gray.300',
      p: '4',
    },
    image: {
      display: 'flex',
      alignItems: 'start',
    },
    content: {
      display: 'grid',
      gap: '2',
      alignContent: 'space-between',
      marginLeft: '2',
      width: 'full',
    },
    name: {
      fontWeight: 'bold',
    },
    detail: {
      display: 'flex',
      width: 'full',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '1',
      fontSize: 'sm',
      color: 'link',
      flexShrink: '0',
    },
    select: {
      marginLeft: '3',
      display: 'flex',
      width: 'full',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    price: {
      fontSize: 'lg',
      fontWeight: 'bold',
    },
    selectbox: {
      marginLeft: '3',
    },
    description: {
      marginTop: '2',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      bg: 'gray.100',
      p: '3',
      fontSize: 'sm',
    },
  },
})

function SdMenuCard({
  option,
  selectedOptions,
  onSelect,
}: {
  option: ReserveInputOptionMenu
  selectedOptions: ReserveOptionMenuInput[]
  onSelect: (e: React.ChangeEvent<HTMLSelectElement>, option: ReserveInputOptionMenu) => void
}) {
  const { opened, toggle, props } = useMenuCard(option, selectedOptions, onSelect)
  const classes = sdMenuCardStyles()

  return (
    <div className={classes.container}>
      <div className={classes.image}>
        {option.optionMenu.image ? (
          <Image
            className={css({ rounded: 'default' })}
            alt="オプションイメージ"
            src={option.optionMenu.image.url}
            width={70}
            height={70}
          />
        ) : (
          <NoImage width={70} height={70} />
        )}
        <div className={classes.content}>
          <p className={classes.name}>{option.optionMenu.name}</p>
          <div className={classes.detail}>
            {option.optionMenu.description && (
              <p className={classes.button} onClick={toggle}>
                {opened ? '閉じる' : '詳細をみる'}
              </p>
            )}
            <div className={classes.select}>
              <p className={classes.price}>
                {/* @see: https://github.com/ikyucom/macaron-frontend/pull/2233#discussion_r1479296948 */}
                {option.optionMenu.price === 0 ? '無料' : money(option.optionMenu.price) + '円'}
              </p>
              <div className={classes.selectbox}>
                <SelectBox width={78} {...props} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {opened && <div className={classes.description}>{option.optionMenu.description}</div>}
    </div>
  )
}

const pcMenuCardStyles = sva({
  slots: [
    'container',
    'image',
    'content',
    'heading',
    'name',
    'select',
    'price',
    'button',
    'description',
  ],
  base: {
    container: {
      borderRadius: 'md',
      border: 'medium',
      borderColor: 'gray.300',
      p: '4',
    },
    image: {
      display: 'flex',
      alignItems: 'start',
    },
    content: {
      alignContent: 'space-between',
      ml: '3',
      width: 'full',
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      columnGap: '2',
    },
    name: {
      fontWeight: 'bold',
      fontSize: 'md',
      flex: '1',
    },
    select: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      columnGap: '2',
    },
    price: {
      fontSize: 'lg',
      width: '[70px]',
      textAlign: 'right',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '1',
      color: 'link',
      fontSize: 'sm',
      mt: '2',
    },
    description: {
      marginTop: '2',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: 'sm',
      bg: 'gray.100',
      p: '3',
      rounded: 'sm',
    },
  },
})

function PcMenuCard({
  option,
  selectedOptions,
  onSelect,
}: {
  option: ReserveInputOptionMenu
  selectedOptions: ReserveOptionMenuInput[]
  onSelect: (e: React.ChangeEvent<HTMLSelectElement>, option: ReserveInputOptionMenu) => void
}) {
  const { opened, toggle, props } = useMenuCard(option, selectedOptions, onSelect)
  const classes = pcMenuCardStyles()

  return (
    <div className={classes.container}>
      <div className={classes.image}>
        {option.optionMenu.image ? (
          <Image
            className={css({ rounded: 'default' })}
            alt="オプションイメージ"
            src={option.optionMenu.image.url}
            width={70}
            height={70}
          />
        ) : (
          <NoImage width={70} height={70} />
        )}
        <div className={classes.content}>
          <div className={classes.heading}>
            <p className={classes.name}>{option.optionMenu.name}</p>
            <div className={classes.select}>
              <p className={classes.price}>
                {option.optionMenu.price === 0 ? '無料' : money(option.optionMenu.price) + '円'}
              </p>
              <SelectBox width={78} {...props} />
            </div>
          </div>
          {option.optionMenu.description && (
            <button type="button" className={classes.button} onClick={toggle}>
              {opened ? (
                <>
                  <p>閉じる</p>
                  <ChevronTopOutlineIcon />
                </>
              ) : (
                <>
                  <p>詳細をみる</p>
                  <ChevronBottomOutlineIcon />
                </>
              )}
            </button>
          )}
          {opened && <p className={classes.description}>{option.optionMenu.description}</p>}
        </div>
      </div>
    </div>
  )
}

type SubmitProps = {
  selectedOptions: ReserveOptionMenuInput[]
}

function SdSubmit({ selectedOptions }: SubmitProps) {
  const submit = useSetOptionMenu()
  const onClick = useCallback(() => {
    submit(selectedOptions)
  }, [selectedOptions, submit])

  return (
    <div
      className={css({
        position: 'fixed',
        bottom: '0',
        width: 'full',
        bg: 'white',
        px: '3',
        pb: '3',
      })}
    >
      <Button color="secondary" spread={true} onClick={onClick}>
        決定する
      </Button>
    </div>
  )
}

const pcSubmitStyles = sva({
  slots: ['container', 'error', 'button', 'cancel'],
  base: {
    container: {
      position: 'sticky',
      left: '0',
      bottom: '0',
      bg: 'white',
      shadow: 'outline.lg',
      p: '6',
    },
    button: {
      spaceY: '5',
      spaceX: '4',
      px: '6',
      pt: '4',
      pb: '10',
    },
    cancel: {
      color: 'gray.700',
      py: '3',
      px: '2',
      rounded: 'md',
      transition: '[all 0.2s]',
      _hover: {
        bg: 'gray.200',
      },
    },
  },
})

function PcSubmit({ selectedOptions }: SubmitProps) {
  const submit = useSetOptionMenu()
  const back = useBack()
  const onCancel = useCallback(() => {
    back('NEW_RESERVE_INPUT')
  }, [back])
  const onClick = useCallback(() => {
    submit(selectedOptions)
  }, [selectedOptions, submit])
  const classes = pcSubmitStyles()
  return (
    <div className={classes.container}>
      <div className={hstack({ justify: 'flex-end', gap: '4' })}>
        <button type="button" onClick={onCancel} className={classes.cancel}>
          キャンセル
        </button>
        <Button color="secondary" onClick={onClick} size="lg">
          決定する
        </Button>
      </div>
    </div>
  )
}
